// The request LIFECYCLE as data (CHT-2002): the states, the 26 allowed moves, the commands that change a request without moving it, and who
// holds a request in each state. Every other place that needs a fact of the lifecycle asks this file: the planner of `concierge-request.ts`,
// the staff console (what it offers), and the test fakes of the admin and the mobile app (what they accept). A fake keeps storage and shape
// only; it never repeats a move.
//
// The file imports TYPES only, so it stands alone like `state-words.ts`, and the mobile app may import it as
// `@lafayette/domain/request-lifecycle` (development fakes only: `docs/engineering/test-fakes.md`). Do not add a value import.
// Frozen: the database CHECK constraints are generated from these tuples.
export const conciergeRequestStates = Object.freeze([
    "DRAFT",
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
    "COMPLETED",
    "CANCELLED",
    "UNAVAILABLE",
    "DECLINED",
    "EXPIRED",
]);
/** Terminal rows are immutable: renewed work is a new request, never a reopened one. */
export const conciergeRequestTerminalStates = Object.freeze([
    "COMPLETED",
    "CANCELLED",
    "UNAVAILABLE",
    "DECLINED",
    "EXPIRED",
]);
const rule = (id, from, to, command, who) => Object.freeze({
    id,
    from,
    to,
    command,
    customer: who.customer === true,
    staff: Object.freeze([...who.staff]),
    system: Object.freeze([...(who.system ?? [])]),
    capability: who.capability ?? "request.work",
    ...(who.staffCreatedOnly === true ? { staffCreatedOnly: true } : {}),
});
const EXPIRY = ["request-expiry"];
const PAYMENT = ["payment-reconciliation"];
/**
 * The 26 allowed transitions: the 22 of the frozen matrix plus R23 to R26, which the owner approved
 * (a customer or staff cancel while a question or a payment is open, a staff-only cancel of a
 * confirmed request, and expiry while waiting for the customer). The database guard (LF010) allows
 * exactly these (from, to) pairs.
 */
export const conciergeRequestTransitions = Object.freeze([
    rule("R1", "DRAFT", "SUBMITTED", "submit", {
        customer: true,
        staff: ["F", "S"],
        capability: "request.create_for_customer",
    }),
    rule("R2", "SUBMITTED", "ASSIGNED", "assign", {
        staff: ["S"],
        system: ["request-assignment"],
        capability: "request.assign",
    }),
    rule("R3", "ASSIGNED", "IN_PROGRESS", "claim", { staff: ["D", "F", "S"] }),
    rule("R4", "IN_PROGRESS", "AWAITING_CUSTOMER", "await_customer", { staff: ["O", "S"] }),
    rule("R5", "AWAITING_CUSTOMER", "IN_PROGRESS", "respond", { customer: true, staff: ["O", "S"] }),
    rule("R6", "IN_PROGRESS", "PAYMENT_REQUIRED", "require_payment", {
        staff: ["O", "S"],
        system: PAYMENT,
    }),
    rule("R7", "PAYMENT_REQUIRED", "IN_PROGRESS", "resolve_payment", {
        staff: ["O", "S"],
        system: PAYMENT,
    }),
    rule("R8", "IN_PROGRESS", "CONFIRMED", "confirm", { staff: ["O", "S"] }),
    rule("R9", "CONFIRMED", "COMPLETED", "complete", { staff: ["O", "S"] }),
    rule("R10", "DRAFT", "CANCELLED", "cancel", {
        customer: true,
        staff: ["F", "S"],
        capability: "request.create_for_customer",
        staffCreatedOnly: true,
    }),
    rule("R11", "SUBMITTED", "CANCELLED", "cancel", { customer: true, staff: ["S"] }),
    rule("R12", "ASSIGNED", "CANCELLED", "cancel", { customer: true, staff: ["D", "S"] }),
    rule("R13", "IN_PROGRESS", "CANCELLED", "cancel", { customer: true, staff: ["O", "S"] }),
    rule("R14", "SUBMITTED", "UNAVAILABLE", "mark_unavailable", { staff: ["S"] }),
    rule("R15", "SUBMITTED", "DECLINED", "decline", { staff: ["S"] }),
    rule("R16", "SUBMITTED", "EXPIRED", "expire", { staff: ["S"], system: EXPIRY }),
    rule("R17", "ASSIGNED", "UNAVAILABLE", "mark_unavailable", { staff: ["D", "S"] }),
    rule("R18", "ASSIGNED", "DECLINED", "decline", { staff: ["D", "S"] }),
    rule("R19", "ASSIGNED", "EXPIRED", "expire", { staff: ["D", "S"], system: EXPIRY }),
    rule("R20", "IN_PROGRESS", "UNAVAILABLE", "mark_unavailable", { staff: ["O", "S"] }),
    rule("R21", "IN_PROGRESS", "DECLINED", "decline", { staff: ["O", "S"] }),
    rule("R22", "IN_PROGRESS", "EXPIRED", "expire", { staff: ["O", "S"], system: EXPIRY }),
    rule("R23", "AWAITING_CUSTOMER", "CANCELLED", "cancel", { customer: true, staff: ["O", "S"] }),
    rule("R24", "PAYMENT_REQUIRED", "CANCELLED", "cancel", { customer: true, staff: ["O", "S"] }),
    rule("R25", "CONFIRMED", "CANCELLED", "cancel", { staff: ["O", "S"] }),
    rule("R26", "AWAITING_CUSTOMER", "EXPIRED", "expire", { staff: ["O", "S"], system: EXPIRY }),
]);
export function isAllowedRequestTransition(from, to) {
    return conciergeRequestTransitions.some((row) => row.from === from && row.to === to);
}
/** Terminal rows are immutable: nothing moves a request out of one of these states. */
export const isTerminalRequestState = (state) => conciergeRequestTerminalStates.includes(state);
// --- Reading the table ---------------------------------------------------------------------------------------------------------------
/** The rule that a command follows from a state, or `null` when that state does not allow the command. One rule per (state, command). */
export function requestTransitionFor(from, command) {
    return (conciergeRequestTransitions.find((row) => row.from === from && row.command === command) ?? null);
}
/** Every rule of one command. */
export function requestTransitionsOf(command) {
    return conciergeRequestTransitions.filter((row) => row.command === command);
}
/** The states in which a request has an owner. */
export const ownedRequestLifecycleStates = Object.freeze([
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
]);
/** Who holds a request in a state. SUBMITTED: nobody (a supervisor assigns it). ASSIGNED: the designated assignee. Working states: the owner. */
export function requestHolderOfState(state) {
    if (state === "SUBMITTED")
        return "nobody";
    if (state === "ASSIGNED")
        return "designated";
    return ownedRequestLifecycleStates.includes(state) ? "owner" : null;
}
/**
 * The states in which each kind of staff member may drive a MOVE command, read from the transition table. A rule that names the owner (`O`) is
 * the owner's, one that names the designated assignee (`D`) is the assignee's, and a rule for a supervisor alone is the supervisor's. A DRAFT
 * is never a staff matter in the console, so it is left out.
 */
export function requestStatesByHolder(command) {
    const supervisor = [];
    const designated = [];
    const owner = [];
    for (const row of requestTransitionsOf(command)) {
        if (row.from === "DRAFT" || row.staff.length === 0)
            continue;
        if (row.staff.includes("O"))
            owner.push(row.from);
        else if (row.staff.includes("D"))
            designated.push(row.from);
        else
            supervisor.push(row.from);
    }
    return Object.freeze({
        supervisor: Object.freeze(supervisor),
        designated: Object.freeze(designated),
        owner: Object.freeze(owner),
    });
}
/**
 * The states in which each in-state command is allowed. `update_draft`: a DRAFT. `reassign_owner`: ASSIGNED (the designated assignee) and the
 * owned states. `set_priority` and `update_status_summary`: any state that is not terminal. `set_work_status`: IN_PROGRESS only (guard LF055).
 */
export const requestInStateCommandStates = Object.freeze({
    update_draft: Object.freeze(["DRAFT"]),
    reassign_owner: Object.freeze(["ASSIGNED", ...ownedRequestLifecycleStates]),
    set_priority: Object.freeze(conciergeRequestStates.filter((state) => !isTerminalRequestState(state))),
    update_status_summary: Object.freeze(conciergeRequestStates.filter((state) => !isTerminalRequestState(state))),
    set_work_status: Object.freeze(["IN_PROGRESS"]),
});
/** `null` when the command is allowed in the state, else the refusal. A terminal state wins over a plain wrong state (except for a draft edit). */
export function requestInStateRefusal(command, state) {
    if (command === "update_draft")
        return state === "DRAFT" ? null : "request.not_draft";
    if (isTerminalRequestState(state))
        return "request.terminal";
    return requestInStateCommandStates[command].includes(state) ? null : "request.transition_invalid";
}
/** Why a MOVE command is refused from a state, or `null` when a rule allows it. A terminal state answers `request.terminal`. */
export function requestMoveRefusal(command, state) {
    if (isTerminalRequestState(state))
        return "request.terminal";
    return requestTransitionFor(state, command) === null ? "request.transition_invalid" : null;
}
